// ============================================================================
// markdown.js — biztonságos, függőségmentes Markdown-megjelenítés az
// üzenetekhez (2026-10-04, tagi kérésre).
//
// BIZTONSÁG - A LÉNYEG: az eredmény KIZÁRÓLAG DOM-csomópontokból épül
// (createElement + textContent), SOHA nem innerHTML-ből. Egy üzenetben a
// nyers HTML (<script>, <img onerror=...>) ezért egyszerű szöveg marad. Link
// csak http(s) címre jön létre; a `javascript:`, `data:` stb. nem.
//
// TÁMOGATOTT: **félkövér**, *dőlt* / _dőlt_, ~~áthúzott~~, `kód`, ``` kódblokk
// ```, - / * / + felsorolás, 1. számozott lista, > idézet, [szöveg](https://…),
// a csupasz https://… cím (automatikus link) és (2026-10-18) a TÁBLÁZAT:
//   | Fejléc | Fejléc |
//   |--------|:------:|     <- elválasztó sor; a kettőspont a igazítást adja (:---, :---:, ---:)
//   | cella  | cella  |
// A táblázat cellái is formázhatók (félkövér, kód, link). Címsor továbbra sincs.
//
// SZÁNDÉKOSAN NINCS: címsor (# ...). Egy "#"-kel kezdődő üzenet a rendszerben
// már jelent valamit ("nem fordítjuk le", lásd chat.inputHint) - a kettő
// ütközne. Kép (![..](..)) sincs: a képeket a csatolás kezeli.
//
// A FORDÍTÁSSAL: a jelölők a fordításban is megmaradnak - a fordító
// rendszerprompt külön szabálya kéri ezt (lásd geminiClient.js).
// ============================================================================

const HIVATKOZAS_ATTR = { target: "_blank", rel: "noopener noreferrer nofollow" };

function elem(tag, doc, szoveg) {
  const n = doc.createElement(tag);
  if (szoveg !== undefined) n.textContent = szoveg;
  return n;
}

/** Csak http(s) cím engedélyezett - minden más (javascript:, data:, ...) szövegként marad. */
export function biztonsagosCim(url) {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch (e) {
    return null;
  }
}

// A link szövege ne lehessen megtévesztő: ha a szöveg maga is egy CÍMNEK
// látszik, de más, mint a valódi cél, a VALÓDI címet mutatjuk
// ("[https://bank.hu](https://rossz.example)" ne hiteltelenítsen).
function linkSzoveg(szoveg, cim) {
  const urlnakLatszik = /^(https?:\/\/|www\.)/i.test(szoveg.trim());
  return urlnakLatszik && szoveg.trim() !== cim ? cim : szoveg;
}

// Az inline minták, a SORREND számít döntetlennél. Mindegyik { re, epit } -
// az `epit` (egyezés, doc, mélység) csomópontokat ad, vagy null-t, ha az
// egyezés mégsem érvényes (pl. szóközzel határolt csillagok).
const MINTAK = [
  { nev: "kod", re: /`([^`\n]+)`/ },
  { nev: "link", re: /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/ },
  { nev: "url", re: /https?:\/\/[^\s<>"'`]+/ },
  // Vastag DŐLT (v187): ***szöveg*** - a fordítás a forrás **kiemelésének** fordítását így jelöli, hogy az eredetivel összevethető legyen.
  { nev: "kover_dolt", re: /\*\*\*(?=[^\s*])([^*]+?)\*\*\*/ },
  { nev: "kover", re: /\*\*(?=\S)([\s\S]+?)\*\*/ },
  { nev: "athuzott", re: /~~(?=\S)([\s\S]+?)~~/ },
  { nev: "dolt", re: /\*(?=[^\s*])([^*\n]+?)\*/ },
  { nev: "dolt_", re: /_(?=[^\s_])([^_\n]+?)_/ },
];

const SZOKARAKTER = /[\p{L}\p{N}_]/u;

function elozoKarakter(s, index) {
  return index > 0 ? s[index - 1] : "";
}
function kovetkezoKarakter(s, vegIndex) {
  return vegIndex < s.length ? s[vegIndex] : "";
}

/** A legkorábbi ÉRVÉNYES egyezés a `from` pozíciótól, vagy null. */
function kovetkezoEgyezes(s, from) {
  let legjobb = null;
  for (const minta of MINTAK) {
    const re = new RegExp(minta.re.source, "g");
    re.lastIndex = from;
    let m;
    while ((m = re.exec(s)) !== null) {
      const veg = m.index + m[0].length;
      const elotte = elozoKarakter(s, m.index);
      const utana = kovetkezoKarakter(s, veg);
      let ervenyes = true;

      if (minta.nev === "dolt" || minta.nev === "dolt_") {
        const jel = minta.nev === "dolt" ? "*" : "_";
        // Szó közepén (pl. snake_case_nev, 2*3*4) nem dőlt; a szomszéd sem lehet ugyanaz a jel.
        if (SZOKARAKTER.test(elotte) || elotte === jel) ervenyes = false;
        if (SZOKARAKTER.test(utana) || utana === jel) ervenyes = false;
        // A záró jel előtt nem lehet szóköz.
        if (/\s$/.test(m[1])) ervenyes = false;
      } else if (minta.nev === "kover" || minta.nev === "athuzott" || minta.nev === "kover_dolt") {
        if (/\s$/.test(m[1])) ervenyes = false;
      }
      if (ervenyes) {
        if (!legjobb || m.index < legjobb.index) legjobb = { nev: minta.nev, index: m.index, m };
        break; // ennél a mintánál ez a legkorábbi érvényes
      }
      re.lastIndex = m.index + 1;
    }
  }
  return legjobb;
}

// A csupasz cím végéről a mondatvégi írásjelek visszakerülnek a szövegbe
// ("lásd https://pelda.hu." - a pont nem a cím része).
function urlVegeLevag(url) {
  let veg = url.length;
  while (veg > 0 && /[.,;:!?)\]}»”’>]/.test(url[veg - 1])) {
    // A záró ")" csak akkor marad, ha a címben van nyitó párja (pl. wikipedia-cím).
    if (url[veg - 1] === ")" && (url.slice(0, veg).match(/\(/g) || []).length >= (url.slice(0, veg).match(/\)/g) || []).length) break;
    veg--;
  }
  return url.slice(0, veg);
}

function inlineCsomopontok(s, doc, linkEngedett = true) {
  const ki = [];
  let poz = 0;
  const szoveg = (t) => { if (t) ki.push(doc.createTextNode(t)); };

  while (poz < s.length) {
    const e = kovetkezoEgyezes(s, poz);
    if (!e) { szoveg(s.slice(poz)); break; }
    szoveg(s.slice(poz, e.index));
    const m = e.m;
    let hossz = m[0].length;

    if (e.nev === "kod") {
      ki.push(elem("code", doc, m[1]));
    } else if (e.nev === "link" || e.nev === "url") {
      let cim, megjelenit, teljes = m[0];
      if (e.nev === "link") { cim = biztonsagosCim(m[2]); megjelenit = m[1]; }
      else {
        const vagott = urlVegeLevag(m[0]);
        teljes = vagott; hossz = vagott.length;
        cim = biztonsagosCim(vagott); megjelenit = vagott;
      }
      if (!cim || !linkEngedett) {
        szoveg(teljes);
      } else {
        const a = elem("a", doc, linkSzoveg(megjelenit, cim));
        a.setAttribute("href", cim);
        a.setAttribute("title", cim);
        for (const [k, v] of Object.entries(HIVATKOZAS_ATTR)) a.setAttribute(k, v);
        ki.push(a);
      }
    } else if (e.nev === "kover_dolt") {
      const n = doc.createElement("strong");
      const d = doc.createElement("em");
      for (const gyerek of inlineCsomopontok(m[1], doc, false)) d.appendChild(gyerek);
      n.appendChild(d);
      ki.push(n);
    } else {
      const tag = e.nev === "kover" ? "strong" : e.nev === "athuzott" ? "del" : "em";
      const n = doc.createElement(tag);
      // Belül minden jelölés érvényes, KIVÉVE a link (ne legyen <a> az <a>-ban).
      for (const gyerek of inlineCsomopontok(m[1], doc, false)) n.appendChild(gyerek);
      ki.push(n);
    }
    poz = e.index + hossz;
  }
  return ki;
}

function sorokInline(sorok, doc) {
  const ki = [];
  sorok.forEach((sor, i) => {
    if (i > 0) ki.push(doc.createElement("br"));
    for (const n of inlineCsomopontok(sor, doc)) ki.push(n);
  });
  return ki;
}

const KOD_NYITO = /^\s{0,3}```[^`]*$/;
const KOD_ZARO = /^\s{0,3}```\s*$/;
const IDEZET = /^\s{0,3}>\s?(.*)$/;
const FELSOROLAS = /^\s{0,3}[-*+•]\s+(\S.*)$/;
const SZAMOZOTT = /^\s{0,3}(\d{1,3})[.)]\s+(\S.*)$/;
// Táblázat (2026-10-18): fejlécsor + elválasztó sor (---, :---, :---:, ---:) + törzssorok. Az elválasztó sor oszlopszáma egyezik a fejlécével
// (különben nem táblázat, hanem sima szöveg - így egy véletlen "|" nem alakít át semmit).
const TABLA_ELVALASZTO = /^\s{0,3}\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const TABLA_MAX_OSZLOP = 20;

/** Egy táblázatsor cellái: a szélső "|" elhagyható, a "\|" egy cellán belüli "|"; a kódszakaszon (`...`) belüli "|" nem választ el. */
export function tablaCellak(sor) {
  let s = String(sor || "").trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cellak = [];
  let akt = "";
  let kodban = false;
  for (let k = 0; k < s.length; k++) {
    const c = s[k];
    if (c === "\\" && s[k + 1] === "|") { akt += "|"; k++; continue; }
    if (c === "`") kodban = !kodban;
    if (c === "|" && !kodban) { cellak.push(akt.trim()); akt = ""; continue; }
    akt += c;
  }
  cellak.push(akt.trim());
  return cellak;
}

/** A táblázat kezdődik-e az i. sornál? (fejléc + vele azonos oszlopszámú elválasztó) @returns {{igazitas: string[]}|null} */
function tablaKezdet(sorok, i) {
  const fej = sorok[i];
  const elv = sorok[i + 1];
  if (typeof fej !== "string" || typeof elv !== "string" || !fej.includes("|") || !TABLA_ELVALASZTO.test(elv) || !elv.includes("-") || !elv.includes("|")) return null;
  const fejCellak = tablaCellak(fej);
  const elvCellak = tablaCellak(elv);
  if (fejCellak.length !== elvCellak.length || fejCellak.length > TABLA_MAX_OSZLOP) return null;
  const igazitas = elvCellak.map((c) => (c.startsWith(":") && c.endsWith(":") ? "kozep" : c.endsWith(":") ? "jobb" : c.startsWith(":") ? "bal" : ""));
  return { igazitas };
}

/**
 * A szöveg megjelenítendő csomópontjai (blokkok: bekezdés, lista, idézet,
 * kódblokk). Üres szövegre üres tömb.
 *
 * @param {string} szoveg
 * @param {Document} [doc]
 * @returns {Node[]}
 */
export function markdownCsomopontok(szoveg, doc = document) {
  const sorok = String(szoveg || "").replace(/\r\n?/g, "\n").split("\n");
  const ki = [];
  let i = 0;

  while (i < sorok.length) {
    const sor = sorok[i];

    if (sor.trim() === "") { i++; continue; }

    // --- Kódblokk (a zárójel nélküli maradék is kód, mint a legtöbb chatben)
    if (KOD_NYITO.test(sor)) {
      const kod = [];
      i++;
      while (i < sorok.length && !KOD_ZARO.test(sorok[i])) { kod.push(sorok[i]); i++; }
      if (i < sorok.length) i++; // záró ```
      const pre = doc.createElement("pre");
      pre.appendChild(elem("code", doc, kod.join("\n")));
      ki.push(pre);
      continue;
    }

    // --- Idézet
    if (IDEZET.test(sor)) {
      const reszek = [];
      while (i < sorok.length && IDEZET.test(sorok[i])) { reszek.push(IDEZET.exec(sorok[i])[1]); i++; }
      const bq = doc.createElement("blockquote");
      for (const n of sorokInline(reszek, doc)) bq.appendChild(n);
      ki.push(bq);
      continue;
    }

    // --- Táblázat: fejléc + elválasztó + törzssorok (az első üres vagy "|"-t nem tartalmazó sorig)
    const tabla = tablaKezdet(sorok, i);
    if (tabla) {
      const oszlopok = tabla.igazitas.length;
      const doboz = doc.createElement("div");
      doboz.setAttribute("class", "md-tabla");
      const t = doc.createElement("table");
      const sorElem = (cellak, tag) => {
        const tr = doc.createElement("tr");
        for (let k = 0; k < oszlopok; k++) {
          const cella = doc.createElement(tag);
          if (tabla.igazitas[k]) cella.setAttribute("class", "al-" + tabla.igazitas[k]);
          for (const n of inlineCsomopontok(cellak[k] || "", doc)) cella.appendChild(n);
          tr.appendChild(cella);
        }
        return tr;
      };
      const thead = doc.createElement("thead");
      thead.appendChild(sorElem(tablaCellak(sorok[i]), "th"));
      t.appendChild(thead);
      i += 2;
      const tbody = doc.createElement("tbody");
      while (i < sorok.length && sorok[i].trim() !== "" && sorok[i].includes("|")) {
        tbody.appendChild(sorElem(tablaCellak(sorok[i]), "td"));
        i++;
      }
      t.appendChild(tbody);
      doboz.appendChild(t);
      ki.push(doboz);
      continue;
    }

    // --- Felsorolás
    if (FELSOROLAS.test(sor)) {
      const ul = doc.createElement("ul");
      while (i < sorok.length && FELSOROLAS.test(sorok[i])) {
        const li = doc.createElement("li");
        for (const n of inlineCsomopontok(FELSOROLAS.exec(sorok[i])[1], doc)) li.appendChild(n);
        ul.appendChild(li);
        i++;
      }
      ki.push(ul);
      continue;
    }

    // --- Számozott lista
    if (SZAMOZOTT.test(sor)) {
      const ol = doc.createElement("ol");
      const elso = Number(SZAMOZOTT.exec(sor)[1]);
      if (elso !== 1) ol.setAttribute("start", String(elso));
      while (i < sorok.length && SZAMOZOTT.test(sorok[i])) {
        const li = doc.createElement("li");
        for (const n of inlineCsomopontok(SZAMOZOTT.exec(sorok[i])[2], doc)) li.appendChild(n);
        ol.appendChild(li);
        i++;
      }
      ki.push(ol);
      continue;
    }

    // --- Bekezdés: az egymást követő, nem különleges sorok (egy sortörés = <br>)
    const par = [];
    while (
      i < sorok.length && sorok[i].trim() !== "" &&
      !KOD_NYITO.test(sorok[i]) && !IDEZET.test(sorok[i]) &&
      !FELSOROLAS.test(sorok[i]) && !SZAMOZOTT.test(sorok[i]) && !tablaKezdet(sorok, i)
    ) { par.push(sorok[i]); i++; }
    const p = doc.createElement("p");
    for (const n of sorokInline(par, doc)) p.appendChild(n);
    ki.push(p);
  }
  return ki;
}

/**
 * A formázás NÉLKÜLI, egyszerű szöveg - előnézethez, kereséshez, idézethez.
 * A tartalom marad, csak a jelölők tűnnek el.
 */
export function markdownSima(szoveg) {
  let s = String(szoveg || "").replace(/\r\n?/g, "\n");
  // kódblokk: a belseje marad
  s = s.replace(/^\s{0,3}```[^\n]*\n([\s\S]*?)(?:\n\s{0,3}```\s*$|$)/gm, "$1");
  s = s.replace(/`([^`\n]+)`/g, "$1");
  s = s.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1");
  s = s.replace(/\*\*\*(?=[^\s*])([^*]+?)\*\*\*/g, "$1");
  s = s.replace(/\*\*(?=\S)([\s\S]+?)\*\*/g, "$1");
  s = s.replace(/~~(?=\S)([\s\S]+?)~~/g, "$1");
  s = s.replace(/(^|[^\p{L}\p{N}_*])\*(?=[^\s*])([^*\n]+?)\*(?![\p{L}\p{N}_*])/gu, "$1$2");
  s = s.replace(/(^|[^\p{L}\p{N}_])_(?=[^\s_])([^_\n]+?)_(?![\p{L}\p{N}_])/gu, "$1$2");
  s = s.replace(/^\s{0,3}>\s?/gm, "");
  s = s.replace(/^\s{0,3}[-*+•]\s+/gm, "");
  s = s.replace(/^\s{0,3}\d{1,3}[.)]\s+/gm, "");
  // A táblázatot a listajelek lehúzása UTÁN alakítjuk át: a cella eleji "1. " nem listajel.
  // Táblázat: az elválasztó sor (|---|---|) eltűnik, a "| a | b |" sorokból "a · b" lesz (előnézet, keresés, idézet).
  s = s.replace(/^\s{0,3}\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*(\n|$)/gm, "");
  s = s.replace(/^\s*\|(.+)\|\s*$/gm, (_, belso) => belso.split("|").map((c) => c.trim()).join(" · "));
  return s;
}
